import React, {useContext, useEffect, useState, useCallback, useMemo} from "react";
import BedRockContext from "../../BedRockContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {GenericTable} from "../generic/table/GenericTable";
import {defaultList} from "../generic/InitialStates";
import {HeaderColumnFormatter} from "../../column-formatters/HeaderColumnFormatter";
import {withSyncDot} from "../../column-formatters/SyncStatusColumnFormatter";
import {ENTITY_ID_INVENTORY_ITEM} from "../generic/Constants";
import {SyncProgressDialog} from "../../generic-components/SyncProgressDialog";
import {useSyncOperations} from "../../hooks/useSyncOperations";
import {useSyncEnabled} from "../../hooks/useSyncEnabled";

export function ServiceItemsPage() {
    const myData = useContext(BedRockContext);
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);

    // Check if entity is sync-enabled and get push/pull capabilities
    const {syncEnabled, pushEnabled, pullEnabled} = useSyncEnabled(ENTITY_ID_INVENTORY_ITEM);

    const reloadData = useCallback(() => {
        setReload(prev => prev + 1);
    }, []);

    // Use sync operations hook with push/pull capabilities
    const {
        showDialog: showSyncDialog,
        syncType,
        isInProgress,
        syncResult,
        pushToServer,
        pullFromServer,
        closeDialog: closeSyncDialog
    } = useSyncOperations(ENTITY_ID_INVENTORY_ITEM, reloadData, { pushEnabled, pullEnabled });

    const profileBreadCrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // Build columns dynamically based on sync enabled status
    const columns = useMemo(() => {
        const baseColumns = [
            {
                dataField: "itemName",
                text: "Item Name",
                search: true,
                formatter: syncEnabled ? withSyncDot(HeaderColumnFormatter) : HeaderColumnFormatter
            },
            {
                dataField: "hsnCode",
                text: "HSN Code",
            },
            {
                dataField: "price",
                text: "Price",
                formatter: (cell) => cell ? `₹${parseFloat(cell).toFixed(2)}` : "₹0.00"
            },
            {
                dataField: "unitSymbol",
                text: "Unit",
                formatter: (cell, row) => {
                    if (cell) {
                        return cell;
                    } else if (row.unitId) {
                        return row.unitId;
                    } else {
                        return "No unit";
                    }
                }
            },
        ];

        return baseColumns;
    }, [syncEnabled]);

    useEffect(() => {
        if (myData && myData.setBaseData) {
            // Build toolbar buttons based on sync capabilities
            const toolBarButtons = [];

            if (syncEnabled && pushEnabled) {
                toolBarButtons.push({
                    name: "Push to Server",
                    color: "info",
                    icon: "fa fa-cloud-upload-alt",
                    onClick: pushToServer,
                });
            }

            if (syncEnabled && pullEnabled) {
                toolBarButtons.push({
                    name: "Pull from Server",
                    color: "success",
                    icon: "fa fa-cloud-download-alt",
                    onClick: pullFromServer,
                });
            }

            myData.setBaseData({toolBarButtons});
        }

        setPageLoadComplete(true);
    }, [pushToServer, pullFromServer, syncEnabled, pushEnabled, pullEnabled]);

    const BaseCard = () => (
        <div className="card card-body">
            {pageLoadComplete && (
                <>
                    <div className="alert alert-info d-flex align-items-center mb-4">
                        <i className="fas fa-info-circle me-3 fs-4"></i>
                        <div>
                            <strong>Service Items - View Only</strong>
                            <br />
                            <small>
                                This page displays service items synchronized from the server.
                                Items are read-only and managed from the server application.
                                {syncEnabled && " Use Pull from Server to get the latest updates."}
                            </small>
                        </div>
                    </div>
                    <GenericTable
                        reload={reload}
                        defaultData={defaultList}
                        columns={columns}
                        entityId={ENTITY_ID_INVENTORY_ITEM}
                        fixedFilterColumn="inventoryType"
                        fixedFilterData="SERVICE"
                    />
                </>
            )}
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Service Items</PageTitle>
            <BaseCard/>
            {syncEnabled && (
                <SyncProgressDialog
                    show={showSyncDialog}
                    onClose={closeSyncDialog}
                    syncType={syncType}
                    isInProgress={isInProgress}
                    syncResult={syncResult}
                />
            )}
        </>
    );
}
